import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import { Users, Eye, Film } from "lucide-react";
import { heartbeat, type PublicStats as Stats } from "@/lib/api";
import { STATS } from "@/constants/testIds";

const VISITOR_KEY = "ss_visitor_id";

function visitorId(): string {
  let id = localStorage.getItem(VISITOR_KEY);
  if (!id) {
    id = `v_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
    localStorage.setItem(VISITOR_KEY, id);
  }
  return id;
}

const fmt = (n: number) => new Intl.NumberFormat("it-IT").format(n);

// Real public metrics: registered accounts, persistent view counter, completed
// renders and live presence. No simulated or hardcoded values.
export function PublicStats() {
  const [stats, setStats] = useState<Stats | null>(null);
  const counted = useRef(false);

  useEffect(() => {
    const id = visitorId();
    let alive = true;
    const ping = async (countView: boolean) => {
      try {
        const data = await heartbeat(id, countView);
        if (alive) setStats(data);
      } catch {
        /* stats are non-critical */
      }
    };
    const isNewSession = !sessionStorage.getItem("ss_view_counted");
    if (isNewSession && !counted.current) {
      counted.current = true;
      sessionStorage.setItem("ss_view_counted", "1");
      ping(true);
    } else {
      ping(false);
    }
    const timer = setInterval(() => ping(false), 20000);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, []);

  const { t } = useTranslation();
  if (!stats) return null;

  const items = [
    { icon: Users, label: t("stats.registered"), value: stats.registered_users, testId: STATS.registered },
    { icon: Eye, label: t("stats.views"), value: stats.views, testId: STATS.views },
    { icon: Film, label: t("stats.videos"), value: stats.videos_generated, testId: STATS.videos },
  ];

  return (
    <div data-testid={STATS.panel}>
      <div className="glass pointer-events-none fixed bottom-5 left-5 z-40 flex flex-row items-center gap-1 rounded-full px-2 py-1.5 !border-[#4d8fff]/30 !shadow-[0_0_0_1px_rgba(46,123,255,0.08),0_20px_50px_-24px_rgba(0,0,0,0.9),0_0_40px_-16px_rgba(46,123,255,0.55)] sm:gap-2">
        {items.map(({ icon: Icon, label, value, testId }, i) => (
          <div
            key={label}
            data-testid={testId}
            aria-label={label}
            title={label}
            className="flex items-center gap-2 pl-1 pr-3"
          >
            {i > 0 && <span aria-hidden className="-ml-2 mr-1 h-4 w-px bg-[#2E7BFF]/25" />}
            <span className="grid h-7 w-7 place-items-center text-[#9CC8FF]">
              <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />
            </span>
            <span className="font-display text-sm font-bold tabular-nums tracking-wide text-foreground">
              {fmt(value)}
            </span>
          </div>
        ))}
      </div>

      <div
        data-testid={STATS.online}
        className="glass pointer-events-none fixed bottom-5 right-5 z-40 inline-flex items-center gap-2.5 rounded-full !border-[#3DDC84]/40 !bg-[linear-gradient(180deg,rgba(61,220,132,0.10),rgba(7,11,20,0.9))] px-4 py-2 !shadow-[0_0_0_1px_rgba(61,220,132,0.08),0_20px_50px_-24px_rgba(0,0,0,0.9),0_0_36px_-12px_rgba(61,220,132,0.6)]"
      >
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#3DDC84]/60" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-[#3DDC84]" style={{ boxShadow: "0 0 8px #3DDC84" }} />
        </span>
        <span className="font-display text-xs font-semibold uppercase tracking-[0.22em] text-[#3DDC84] tabular-nums">
          {fmt(stats.online)} online
        </span>
      </div>
    </div>
  );
}
